import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const TierContext = createContext();
const TOKEN_KEY = 'alphagrade_auth_token';

export function TierProvider({ children }) {
  // `loaded` distinguishes "this user has no allowance" from "we have not
  // asked yet". Without it, the defaults below ARE the answer on first render,
  // so a Premium user hard-loading a gated page sees the upgrade wall before
  // the real page, and sees it forever if /api/auth/tier happens to fail.
  // Telling a paying customer to buy what they already own is the same trust
  // failure as f41a52b; gated screens should wait rather than guess.
  const [loaded, setLoaded] = useState(false);
  const [tierInfo, setTierInfo] = useState({
    tier: 'free',
    name: 'Free',
    portfolio_positions: 5,
    builder_positions: 0,
    screens_per_day: 2,
    chats_per_day: 0,
    features: [],
    screens_used: 0,
    chats_used: 0,
  });

  const fetchTier = useCallback(async () => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) {
      // Anonymous: the free defaults are the correct answer, immediately.
      setLoaded(true);
      return;
    }
    try {
      const resp = await fetch('/api/auth/tier', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (resp.ok) {
        const data = await resp.json();
        setTierInfo(data);
      }
    } catch {
      // Network failure — leave the previous tier info in place rather than
      // downgrading the user on a blip.
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => {
    fetchTier();
  }, [fetchTier]);

  const hasFeature = useCallback((feature) => {
    return tierInfo.features.includes(feature);
  }, [tierInfo.features]);

  const value = {
    ...tierInfo,
    loaded,
    hasFeature,
    refreshTier: fetchTier,
  };

  return (
    <TierContext.Provider value={value}>
      {children}
    </TierContext.Provider>
  );
}

export function useTier() {
  const context = useContext(TierContext);
  if (!context) {
    throw new Error('useTier must be used within a TierProvider');
  }
  return context;
}
